/**
 * THE QUEUE — "I want it when whoever's on it finishes" (docs/01 §5.1).
 *
 * Two things this screen refuses to do quietly:
 *
 *  - **A claim window is always visible as a countdown.** The window is the only
 *    thing stopping the queue stalling forever on someone who went home, so the
 *    person holding it sees exactly how long they have, and everybody behind
 *    them sees it too.
 *  - **A booking pauses the queue, and says so.** Silently stalling — no
 *    countdown, no promotion, no explanation — is precisely the failure the
 *    pause is supposed to be. The banner names the booking's end time, and the
 *    domain freezes the claim window across the booked slot so nobody is expired
 *    at the instant the table frees up.
 *
 * Everything is derived by `queueSnapshot`, which is pure and takes `now`
 * explicitly, so this screen renders the same frame for the same instant on
 * every device.
 */

import {
  SpaceEmpty,
  SpaceStyles,
  formatCountdown,
  formatDuration,
  mutationCtx,
  nameLookup,
  timeOfDay,
  useAction,
  useNow,
  useSpace,
  type SpaceScreenProps,
} from './spaceData.js'
import { mutations } from '../../lib/mutations.js'
import {
  occupancyDurationMs,
  openOccupancy,
  queueFor,
  queuePosition,
  queueSnapshot,
} from '@office/domain/resources'
import type { Booking, Occupancy, PersonId, QueueEntry, Resource } from '@office/domain'

export interface QueueProps extends SpaceScreenProps {
  /** Countdown tick. 0 freezes it — tests pass 0 and assert one exact frame. */
  readonly tickMs?: number
}

export function Queue(props: QueueProps) {
  const { db, clock, actorId } = props
  const now = useNow(clock, props.tickMs ?? 1000)
  const space = useSpace(db)
  const run = useAction()
  const nameOf = nameLookup(space.people)

  const queueable = space.resources.filter(r => r.queueable && r.active)

  return (
    <section className="sp" aria-labelledby="sp-queue-h">
      <SpaceStyles />
      <div className="sp-head">
        <h2 className="sp-title" id="sp-queue-h">
          Queue
        </h2>
        <span className="sp-note">Order is join time</span>
      </div>

      {queueable.length === 0 ? (
        <SpaceEmpty mark="🏓" hint="Mark a resource queueable — the ping pong table, the PlayStation — and whoever is next lines up here.">
          Nothing to queue for yet.
        </SpaceEmpty>
      ) : (
        queueable.map(resource => (
          <ResourceQueue
            key={resource.id}
            resource={resource}
            now={now}
            actorId={actorId}
            nameOf={nameOf}
            entries={space.queue}
            bookings={space.bookings}
            occupancies={space.occupancies}
            claimWindowMinutes={space.settings.queueClaimWindowMinutes}
            onJoin={() => run(() => mutations.joinQueue(mutationCtx(props), String(resource.id)))}
            onLeave={entryId => run(() => mutations.leaveQueue(mutationCtx(props), entryId))}
            onClaim={entryId => run(() => mutations.claimQueue(mutationCtx(props), entryId))}
            onEndTurn={(entryId, releaseOccupancy) => {
              // Two writes, because ending a turn is two facts: the queue entry
              // is finished (which is what lets the next person be promoted)
              // and the table is no longer occupied.
              run(() => mutations.leaveQueue(mutationCtx(props), entryId))
              if (releaseOccupancy) {
                run(() => mutations.endOccupancy(mutationCtx(props), String(resource.id)))
              }
            }}
          />
        ))
      )}
    </section>
  )
}

interface ResourceQueueProps {
  readonly resource: Resource
  readonly now: number
  readonly actorId: string
  readonly nameOf: (id: string) => string
  readonly entries: readonly QueueEntry[]
  readonly bookings: readonly Booking[]
  readonly occupancies: readonly Occupancy[]
  readonly claimWindowMinutes: number
  readonly onJoin: () => void
  readonly onLeave: (entryId: string) => void
  readonly onClaim: (entryId: string) => void
  readonly onEndTurn: (entryId: string, releaseOccupancy: boolean) => void
}

export function ResourceQueue({
  resource,
  now,
  actorId,
  nameOf,
  entries,
  bookings,
  occupancies,
  claimWindowMinutes,
  onJoin,
  onLeave,
  onClaim,
  onEndTurn,
}: ResourceQueueProps) {
  const context = { bookings, occupancies }
  const snapshot = queueSnapshot(entries, resource.id, now, context)
  const live = queueFor(entries, resource.id)
  const mine = live.find(e => String(e.personId) === actorId) ?? null
  const position = queuePosition(entries, resource.id, actorId as PersonId)
  const open = openOccupancy(occupancies, resource.id)
  const claimingIsMe = snapshot.claiming !== null && String(snapshot.claiming.personId) === actorId
  const remaining =
    snapshot.claiming?.expiresAt === null || snapshot.claiming?.expiresAt === undefined
      ? null
      : snapshot.claiming.expiresAt - now
  const windowMs = Math.max(1, claimWindowMinutes * 60_000)

  return (
    <article className="card stack gap-3" aria-labelledby={`sp-q-${resource.id}`}>
      <div className="sp-head">
        <h3 className="sp-card-title" id={`sp-q-${resource.id}`}>
          {resource.name}
        </h3>
        {/* Someone whose turn has started is `playing` in the queue whether or
            not an occupancy row exists — claiming a slot does not open one, on
            this device or on the server. Reading only occupancy would print
            "On now: You" beside a pill saying "Free". */}
        <StatusPill
          paused={snapshot.paused}
          inUse={open !== null || snapshot.onNow !== null}
          waiting={snapshot.waiting.length}
        />
      </div>

      {snapshot.paused ? (
        <p className="sp-banner" role="status">
          Booked{snapshot.pausedUntil === null ? '' : ` until ${timeOfDay(snapshot.pausedUntil)}`} —
          the queue is paused. Nobody is promoted or expired until the booking ends.
        </p>
      ) : null}

      <p className="sp-note" data-testid={`on-now-${resource.id}`}>
        {snapshot.onNow !== null
          ? `On now: ${nameOf(String(snapshot.onNow))}${
              open === null ? '' : ` · ${formatDuration(occupancyDurationMs(open, now))}`
            }`
          : open !== null
            ? `In use · ${formatDuration(occupancyDurationMs(open, now))}`
            : 'Free right now'}
      </p>

      {snapshot.claiming !== null ? (
        <div className="stack gap-2" data-testid={`claim-${resource.id}`}>
          <div className="row gap-3">
            <span className="grow">
              {claimingIsMe
                ? 'Your turn — claim it'
                : `${nameOf(String(snapshot.claiming.personId))} is claiming`}
            </span>
            {remaining !== null ? (
              <span className="sp-countdown" aria-label={`${formatCountdown(remaining)} left to claim`}>
                {formatCountdown(remaining)}
              </span>
            ) : null}
          </div>
          {remaining !== null ? (
            <div
              className="sp-meter"
              role="progressbar"
              aria-label="Claim window remaining"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(clamp01(remaining / windowMs) * 100)}
              // Without this a screen reader announces "90 percent", which is
              // not the fact anyone needs. The deadline is the fact.
              aria-valuetext={`${formatCountdown(remaining)} left to claim`}
            >
              <span style={{ width: `${clamp01(remaining / windowMs) * 100}%` }} />
            </div>
          ) : null}
          {claimingIsMe ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onClaim(String(snapshot.claiming?.entryId ?? ''))}
              disabled={snapshot.paused}
            >
              Claim your turn
            </button>
          ) : null}
        </div>
      ) : null}

      {snapshot.waiting.length > 0 ? (
        <ol className="sp-list sp-rows" aria-label={`Waiting for ${resource.name}`}>
          {snapshot.waiting.map((personId, i) => (
            <li key={`${personId}-${i}`} className="sp-row">
              <span className="sp-rank">{i + 1}</span>
              <span className="grow">
                {nameOf(String(personId))}
                {String(personId) === actorId ? ' (you)' : ''}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="sp-note">Nobody waiting.</p>
      )}

      {position !== null ? (
        <p className="sp-banner" data-tone="info" role="status">
          You are {ordinal(position)} in line.
        </p>
      ) : null}

      {mine === null ? (
        <button type="button" className="btn btn-primary" onClick={onJoin}>
          Join the queue
        </button>
      ) : mine.status === 'playing' ? (
        // Nothing else on the phone frees the table, and a turn that never ends
        // is a queue that never moves.
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onEndTurn(String(mine.id), open !== null)}
        >
          End your turn
        </button>
      ) : (
        <button type="button" className="btn" onClick={() => onLeave(String(mine.id))}>
          Leave the queue
        </button>
      )}
    </article>
  )
}

function StatusPill({
  paused,
  inUse,
  waiting,
}: {
  paused: boolean
  inUse: boolean
  waiting: number
}) {
  if (paused) return <span className="pill pill-warn">Paused</span>
  if (inUse) return <span className="pill">In use</span>
  if (waiting > 0) return <span className="pill">{waiting} waiting</span>
  return <span className="pill pill-ok">Free</span>
}

const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0)

export function ordinal(n: number): string {
  const rem100 = n % 100
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}
